import { ProCustomer, ProJob, Quote } from '../types/pro'
import { QualityReport } from '../types/quality'
import { navGroups } from '../data/navigation'
import { View } from '../types/view'
import { contactSearchText } from './contacts'

export type SearchResultKind =
  | 'customer'
  | 'job'
  | 'quote'
  | 'quality'
  | 'page'

export interface SearchResult {
  id: string
  kind: SearchResultKind
  /** Group heading shown above the result, e.g. "Kunder". */
  group: string
  title: string
  subtitle?: string
  /** Reference shown on the right, e.g. "K-2026-0012". */
  reference?: string
  view: View
  /** Passed back so the app can select the underlying record. */
  recordId?: string
}

const MAX_PER_GROUP = 4

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .trim()
}

function matches(query: string, ...fields: (string | undefined)[]): boolean {
  const q = normalize(query)
  return fields.some((field) => field && normalize(field).includes(q))
}

interface SearchInput {
  query: string
  customers: ProCustomer[]
  jobs: ProJob[]
  quotes: Quote[]
  qualityReports: QualityReport[]
  isAdmin: boolean
}

export function searchEverything({
  query,
  customers,
  jobs,
  quotes,
  qualityReports,
  isAdmin,
}: SearchInput): SearchResult[] {
  if (query.trim().length < 2) return []

  const results: SearchResult[] = []

  customers
    .filter((customer) =>
      matches(
        query,
        customer.name,
        customer.customerNo,
        customer.address,
        customer.city,
        customer.email,
        customer.phone,
        // Finding a customer by the name of the person you spoke to.
        contactSearchText(customer),
      ),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((customer) => {
      results.push({
        id: `customer-${customer.id}`,
        kind: 'customer',
        group: 'Kunder',
        title: customer.name,
        subtitle: [customer.address, customer.city]
          .filter(Boolean)
          .join(', '),
        reference: customer.customerNo,
        view: 'pro-customer-detail',
        recordId: customer.id,
      })
    })

  jobs
    .filter((job) =>
      matches(query, job.title, job.jobNo, job.customerName, job.address),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((job) => {
      results.push({
        id: `job-${job.id}`,
        kind: 'job',
        group: 'Opgaver',
        title: job.title,
        subtitle: job.customerName,
        reference: job.jobNo,
        view: 'pro-job-detail',
        recordId: job.id,
      })
    })

  quotes
    .filter((quote) =>
      matches(query, quote.title, quote.quoteNo, quote.customerName),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((quote) => {
      results.push({
        id: `quote-${quote.id}`,
        kind: 'quote',
        group: 'Tilbud',
        title: quote.title || 'Tilbud uden titel',
        subtitle: quote.customerName,
        reference: quote.quoteNo,
        view: 'pro-quote-editor',
        recordId: quote.id,
      })
    })

  qualityReports
    .filter((report) =>
      matches(query, report.title, report.reportId, report.customer.name),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((report) => {
      results.push({
        id: `quality-${report.id}`,
        kind: 'quality',
        group: 'Kvalitetssikring',
        title: report.title,
        subtitle: report.customer.name,
        reference: report.reportId,
        view: 'quality-detail',
        recordId: report.id,
      })
    })

  navGroups
    .flatMap((group) => group.items)
    .filter((item) => !item.adminOnly || isAdmin)
    .filter((item) => matches(query, item.label))
    .slice(0, MAX_PER_GROUP)
    .forEach((item) => {
      results.push({
        id: `page-${item.id}`,
        kind: 'page',
        group: 'Gå til',
        title: item.label,
        view: item.id,
      })
    })

  return results
}

/** Results grouped in display order, preserving the order they were added. */
export function groupResults(
  results: SearchResult[],
): { group: string; items: SearchResult[] }[] {
  const groups: { group: string; items: SearchResult[] }[] = []
  results.forEach((result) => {
    const existing = groups.find((g) => g.group === result.group)
    if (existing) {
      existing.items.push(result)
    } else {
      groups.push({ group: result.group, items: [result] })
    }
  })
  return groups
}
